import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MarkdownDirective } from '../../directives'
import { ModalComponent, ModalService } from '../modal'
import {
  ConfirmationModalDefinitionWithCallbacks,
  ConfirmationModalService,
  DEFAULT_CONFIRMATION_MODAL_DEFINITION,
} from './confirmation-modal.service'

/**
 * This component is responsible for rendering the confirmation modal. It should be treated as a singleton and be placed
 * at the root of the application. It listens for changes to the modal definition managed by the `ConfirmationModalService`
 * and renders the modal accordingly.
 *
 * **Important:** This component should not be used directly in templates. Instead, use the `ConfirmationModalService` to
 * open the modal.
 */
@Component({
  selector: 'ftr-confirmation-modal',
  imports: [ModalComponent, MarkdownDirective],
  templateUrl: './confirmation-modal.component.html',
})
export class ConfirmationModalComponent implements OnInit {
  protected modalDefinition = signal<ConfirmationModalDefinitionWithCallbacks<unknown>>(
    DEFAULT_CONFIRMATION_MODAL_DEFINITION,
  )

  protected modalTextContent = computed(() => {
    const content = this.modalDefinition().content
    return typeof content === 'string' ? content : undefined
  })

  private modalPortalRef = viewChild.required('modalPortal', { read: TemplateRef })
  private modalContentsVcr = viewChild.required('modalContents', { read: ViewContainerRef })
  #destroyRef = inject(DestroyRef)

  constructor(
    private readonly modalService: ModalService,
    private readonly confirmationModalService: ConfirmationModalService,
    private readonly viewContainerRef: ViewContainerRef,
  ) {}

  ngOnInit(): void {
    this.confirmationModalService
      .observeModalDefinition()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(modalDef => {
        // First cancel any existing modals
        this.modalDefinition().onCancel()

        // Now set up the new one
        this.modalDefinition.set(modalDef)

        this.modalService.open(this.modalPortalRef(), this.viewContainerRef)

        // dynamically create the content component
        if (typeof modalDef.content !== 'string') {
          const contentComponent = this.modalContentsVcr().createComponent(modalDef.content.type)
          Object.entries(modalDef.content.inputs || {}).forEach(([key, value]) => {
            contentComponent.setInput(key as keyof typeof modalDef.content.inputs, value)
          })
        }
      })

    this.#destroyRef.onDestroy(() => {
      this.modalService.close()
    })
  }

  onAction(): void {
    this.modalDefinition().onConfirm()
    this.modalService.close()
    this.modalDefinition.set(DEFAULT_CONFIRMATION_MODAL_DEFINITION)
  }

  onClose(): void {
    this.modalDefinition().onCancel()
    this.modalDefinition.set(DEFAULT_CONFIRMATION_MODAL_DEFINITION)
  }
}
